<template>
  <div class="tabs-layout">
    <draggable
      v-if="!isBareRoute"
      tag="ul"
      class="nav nav-tabs small-tabs bg-transparent px-0 pt-1"
      :list="tabs"
    >
      <li v-for="(tab, index) in tabs" :key="tab.fullPath" class="nav-item">
        <a
          class="nav-link"
          :class="{ active: tab.fullPath === $route.fullPath, 'nav-link--pinned': tab.pinned }"
          @click="goToTab(tab)"
        >
          <span
            v-if="!tab.controlsInHeader"
            @click.stop="togglePin(tab)"
            class="tab-pin-toggle ml-2"
            :class="{ 'tab-pin-toggle--active': tab.pinned }"
            :title="tab.pinned ? 'Desfijar' : 'Fijar'"
          >
            <AppIcon name="push-pin" :fill="tab.pinned" />
          </span>

          <TabSubtitleHint
            v-if="tab.subtitle || tab.icon"
            :text="tab.subtitle"
            :icon="tab.icon"
            class="mr-1 d-md-none"
          ></TabSubtitleHint>

          {{ tab.title }}

          <span
            v-if="!tab.controlsInHeader"
            @click.stop="toggleShortcut(tab)"
            class="text-muted ml-2"
            :title="isShortcut(tab) ? 'Ya agregado a accesos directos' : 'Agregar acceso directo'"
          >
            <AppIcon
              :name="isShortcut(tab) ? 'star-fill' : 'star'"
              :class="{ 'text-primary': isShortcut(tab) }"
            />
          </span>

          <span
            @click.stop="closeTab(index)"
            class="text-danger ml-2 close-x"
            title="Cerrar"
          >
            <AppIcon name="x" />
          </span>
        </a>
      </li>
    </draggable>

    <div class="tabs-layout__content p-0 pt-1" :class="{ 'tabs-layout__content--bare': isBareRoute }">
      <div class="tabs-layout__view" :class="{ 'tabs-layout__view--bare': isBareRoute }">
        <keep-alive>
          <router-view :key="$route.fullPath" />
        </keep-alive>
      </div>
    </div>
  </div>
</template>


<script>
  import {EventBus} from '@/handler/event-bus'
  import draggable from 'vuedraggable'
  import shortcuts from '@/handler/shortcuts'
  import TabSubtitleHint from '@/components/inc/tabs/TabSubtitleHint'
  import Session from '@/handler/session'
  import VIEWS_REGISTRY from '@/router/viewsRegistry'

  // Índice inverso nombre-de-ruta -> reference, armado una sola vez a partir
  // del mismo VIEWS_REGISTRY que ya usa el buscador (Ctrl+K) para resolver
  // permissions.items a una ruta navegable — acá se usa al revés: dado
  // $route.name, encontrar el reference para poder consultar
  // Session.getViewMetadataByReference() y saber si es mobile_first.
  const ROUTE_NAME_TO_REFERENCE = {}
  Object.keys(VIEWS_REGISTRY).forEach(moduleKey => {
    var moduleViews = VIEWS_REGISTRY[moduleKey]
    Object.keys(moduleViews).forEach(reference => {
      ROUTE_NAME_TO_REFERENCE[moduleViews[reference].route] = reference
    })
  })

  export default {
    components: {
      draggable,
      TabSubtitleHint,
    },
    data() {
      return {
        tabs: [],
        shortcutPaths: []
      }
    },
    computed: {
      // "bare" = ruta sin tab persistente ni card de .tabs-layout__view (ver
      // isNonTabRoute). No es exclusivo de "Mi espacio": las pantallas
      // first-mobile (alta/edición de un único registro, pensadas para
      // usarse angostas) tampoco siguen la regla de tabs — se marcan con
      // meta.bare en su propio router.js en vez de hardcodear cada nombre acá.
      isBareRoute() {
        return this.isNonTabRoute(this.$route)
      }
    },
    created () {
      // Agrega la primera vista como tab inicial — "Mi espacio" es la única
      // vista que no se persiste como tab (es la pantalla de inicio/base,
      // no tiene sentido poder cerrarla, fijarla o acumularla en la barra).
      if (!this.isNonTabRoute(this.$route)) {
        this.tabs.push({
          title: (this.$route.meta && this.$route.meta.title && this.$route.meta.title.toUpperCase()) || (this.$route.name && this.$route.name.toUpperCase()) || this.$route.path.toUpperCase(),
          subtitle: '',
          icon: '',
          fullPath: this.$route.fullPath,
          pinned: false,
          controlsInHeader: false
        });
      }

      this.loadShortcuts()

      EventBus.$on('Update:TabTitle', title => {
        this.refreshActiveTabTitle(title.toUpperCase())
      })
      EventBus.$on('Update:TabMetadata', metadata => {
        this.refreshActiveTabMetadata(metadata)
      })
      EventBus.$on('Tabs:Close', payload => {
        const targetPath = payload && payload.fullPath ? payload.fullPath : this.$route.fullPath
        this.closeTabByPath(targetPath, payload && payload.goTo ? payload.goTo : null)
      })
      EventBus.$on('Shortcuts:updated', list => {
        this.shortcutPaths = (list || []).map(item => item.fullPath)
        this.emitActiveTabState()
      })
      // Vistas que muestran fijar/acceso directo en su propia cabecera
      // (por ahora, los dashboards vía TabHeaderActions.vue) en vez del
      // tab: se anuncian solas al montar/reactivarse, así el tab bar no
      // necesita saber qué vistas migraron y cuáles no.
      EventBus.$on('Tab:ControlsInHeader', () => {
        this.setActiveTabControlsInHeader(true)
      })
      EventBus.$on('Tab:RequestActiveState', () => {
        this.emitActiveTabState()
      })
      EventBus.$on('Tab:RequestTogglePin', () => {
        const tab = this.findActiveTab()
        if (tab) this.togglePin(tab)
      })
      EventBus.$on('Tab:RequestToggleShortcut', () => {
        const tab = this.findActiveTab()
        if (tab) this.toggleShortcut(tab)
      })
    },
    watch: {
      $route(to) {
        if (this.isNonTabRoute(to)) return;

        const exists = this.tabs.find(tab => tab.fullPath === to.fullPath);
        if (exists) return;

        // Busca una tab no fijada para reutilizar
        const index = this.tabs.findIndex(tab => !tab.pinned);
        const newTab = {
          title: (to.meta && to.meta.title && to.meta.title.toUpperCase()) || (to.name && to.name.toUpperCase()) || to.path.toUpperCase(),
          subtitle: '',
          icon: '',
          fullPath: to.fullPath,
          pinned: false,
          controlsInHeader: false
        };

        if (index !== -1) {
          // Reemplaza la no fijada
          this.$set(this.tabs, index, newTab);
        } else {
          // Todas están fijadas, agrega una nueva
          this.tabs.push(newTab);
        }
      }
    },
    methods: {
      isNonTabRoute(route) {
        if (route.name === 'MySpaceStaff') return true

        // mobile_first se configura en el backend, junto a título/subtítulo/
        // ícono de la vista (getView() de cada módulo -> m_modules_permissions),
        // no acá — ver docs/mobile-first-interfaces. Si todavía no sincronizó
        // el metadata (recién logueado, versión vieja en localStorage), esto
        // devuelve false y la vista se ve con tabs hasta que sincronice: no
        // rompe nada, solo no aplica el tratamiento bare todavía.
        var reference = ROUTE_NAME_TO_REFERENCE[route.name]
        if (!reference) return false
        var metadata = Session.getViewMetadataByReference(reference)
        return !!(metadata && metadata.mobile_first)
      },
      loadShortcuts() {
        this.shortcutPaths = shortcuts.all().map(item => item.fullPath)
      },
      isShortcut(tab) {
        return this.shortcutPaths.indexOf(tab.fullPath) !== -1
      },
      toggleShortcut(tab) {
        if (this.isShortcut(tab)) return
        shortcuts.add({ title: tab.title, fullPath: tab.fullPath })
        const list = shortcuts.all()
        this.shortcutPaths = list.map(item => item.fullPath)
        EventBus.$emit('Shortcuts:updated', list)
        this.emitActiveTabState()
      },
      findActiveTab() {
        return this.tabs.find(tab => tab.fullPath === this.$route.fullPath)
      },
      setActiveTabControlsInHeader(value) {
        const tab = this.findActiveTab()
        if (tab) tab.controlsInHeader = value
      },
      // Le contesta a TabHeaderActions.vue (o cualquier control futuro en
      // cabecera) el estado real del tab activo, para no duplicar el
      // pinned/isShortcut en otro lugar.
      emitActiveTabState() {
        const tab = this.findActiveTab()
        EventBus.$emit('Tab:ActiveState', {
          pinned: tab ? !!tab.pinned : false,
          isShortcut: tab ? this.isShortcut(tab) : false,
        })
      },
      goToTab(tab) {
        if (tab.fullPath !== this.$route.fullPath) {
          this.$router.push(tab.fullPath).catch(() => {});
        }
      },
      closeTab(index) {
        const closed = this.tabs.splice(index, 1)[0];
        if (closed.fullPath === this.$route.fullPath) {
          const next = this.tabs[index - 1] || this.tabs[0];
          if (next) this.$router.push(next.fullPath);
        }
      },
      closeTabByPath(fullPath, goTo) {
        const index = this.tabs.findIndex(tab => tab.fullPath === fullPath);
        if(index === -1) {
          if(goTo) this.$router.push(goTo).catch(() => {});
          return
        }
        const closed = this.tabs.splice(index, 1)[0];
        if (closed.fullPath === this.$route.fullPath) {
          if(goTo) {
            this.$router.push(goTo).catch(() => {});
            return
          }
          const next = this.tabs[index - 1] || this.tabs[0];
          if (next) this.$router.push(next.fullPath).catch(() => {});
        }
      },
      refreshActiveTabTitle(title) {
        const currentPath = this.$route.fullPath;
        const index = this.tabs.findIndex(tab => tab.fullPath === currentPath);
        if (index !== -1) {
          this.$set(this.tabs, index, {
            ...this.tabs[index],
            title
          });
          // No renombramos el favorito acá: si el usuario le puso un nombre
          // propio al accesos directo, no tiene que perderlo solo por entrar
          // a la vista. El título de la tab sigue reflejando la página igual.
        }
      },
      refreshActiveTabMetadata(metadata) {
        const currentPath = this.$route.fullPath;
        const index = this.tabs.findIndex(tab => tab.fullPath === currentPath);
        if (index === -1) return;

        const title = (metadata.title || this.tabs[index].title).toUpperCase();
        this.$set(this.tabs, index, {
          ...this.tabs[index],
          title,
          subtitle: metadata.subtitle || '',
          icon: metadata.icon || '',
        });
        // Idem refreshActiveTabTitle: no tocar el título persistido del
        // favorito, solo el de la tab visible.
      },
      togglePin(tab) {
        tab.pinned = !tab.pinned;
        this.emitActiveTabState()
      }
    }
  }
</script>

<style scoped>
  .small-tabs .nav-link {
    font-size: 0.85rem;
    padding: 4px 4px 2px 4px;
    margin-right: 4px;
    border-radius: 4px;
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--app-color-text-secondary);
    transition: background-color 0.2s ease;
  }

  .small-tabs .nav-link.active {
    background-color: var(--app-color-primary-soft);
    border: 1px solid var(--app-color-primary-200);
    border-top: 3px solid var(--app-color-primary);
    padding-top: 2px;
    font-weight: bold;         /* texto en negrita */
    color: var(--app-color-primary-active);
  }

  /* Fijada pero no activa: fondo tenue igual que .active, en el color de
     "warning" en vez de primary, para no confundirla con la tab actual. */
  .small-tabs .nav-link.nav-link--pinned:not(.active) {
    background-color: var(--app-color-warning-soft);
    border: 1px solid var(--app-color-warning-border);
  }

  /* Fijada Y activa: acá el ámbar de "fijada" chocaba contra el celeste de
     "activa", así que en vez de mezclar colores la franja lateral usa el
     mismo primary del fondo/borde de la tab activa, solo que más saturado. */
  .small-tabs .nav-link.nav-link--pinned.active {
    box-shadow: inset 3px 0 0 var(--app-color-primary);
  }

  .tab-pin-toggle {
    color: var(--app-color-text-secondary);
  }

  .tab-pin-toggle--active {
    color: var(--app-color-warning);
  }

  .main > .tabs-layout + * {
    margin-top: 1rem;
  }  

  .small-tabs {
    overflow-x: auto;
    overflow-y: hidden; 
    white-space: nowrap;
    flex-wrap: nowrap;
    scrollbar-width: thin; /* Firefox */
    scrollbar-color: var(--app-color-border-strong) transparent; /* Firefox */
  }

  /* WebKit (Chrome, Edge, Safari) */
  .small-tabs::-webkit-scrollbar {
    height: 4px;
  }

  .small-tabs::-webkit-scrollbar-track {
    background: transparent;
  }

  .small-tabs::-webkit-scrollbar-thumb {
    background-color: var(--app-color-border-strong);
    border-radius: 2px;
  }

  .small-tabs {
    border-bottom: none;
  }
  .small-tabs .nav-link {
    border: none;
  }
  .tabs-layout__content {
    padding: 5px 3px 16px !important;
  }
  .tabs-layout__view {
    min-width: 0;
    padding: 8px;
    border: 1px solid var(--app-color-border);
    border-radius: 12px;
    background: var(--app-color-surface-translucent);
    box-shadow: var(--app-shadow-sm);
  }

  /* Rutas "bare" (ver isBareRoute/isNonTabRoute en el script: "Mi espacio" y
     cualquier ruta con meta.bare, como las pantallas first-mobile) no viven
     dentro de un tab — sin la barra de tabs arriba, tampoco deben quedar
     encerradas en la "card" pensada para el contenido de una tab. Ocupan el
     100% del contenedor libre, sin padding/borde/fondo propios. */
  .tabs-layout__content--bare {
    padding: 0 !important;
  }
  .tabs-layout__view--bare {
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }

  .close-x {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    font-size: 1rem;
    margin-left: 6px;
    padding: 1px;
    border-radius: 50%;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
  }

  .close-x:hover {
    background-color: var(--app-color-danger-soft);
    color: var(--app-color-danger);
  }
</style>
